<template>
  <div
    ref="lottieContainer"
    :style="{ width, height }"
    @mouseenter="playForward"
    @mouseleave="playReverse"
  ></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import lottie from "lottie-web";

const props = defineProps({
  animationData: {
    type: Object,
    required: true,
  },
  width: {
    type: String,
    default: "100%",
  },
  height: {
    type: String,
    default: "100%",
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  // New prop to control scroll trigger behavior
  scrollTrigger: {
    type: Boolean,
    default: false,
  },
  // Threshold for how much of the element needs to be visible (0-1)
  intersectionThreshold: {
    type: Number,
    default: 0.5,
  },
});

const lottieContainer = ref(null);
let animationInstance = null;
let observer = null;

const playForward = () => {
  if (animationInstance) {
    animationInstance.setDirection(1);
    animationInstance.play();
  }
};

const playReverse = () => {
  if (animationInstance) {
    animationInstance.setDirection(-1);
    animationInstance.play();
  }
};

const createAnimation = () => {
  if (lottieContainer.value && props.animationData) {
    animationInstance = lottie.loadAnimation({
      container: lottieContainer.value,
      renderer: "svg",
      loop: false,
      autoplay: props.autoplay && !props.scrollTrigger, // Don't autoplay if scroll trigger is enabled
      animationData: props.animationData,
    });
  }
};

const setupIntersectionObserver = () => {
  if (!props.scrollTrigger || !lottieContainer.value) return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Element is in view, play animation
          playForward();
        }
      });
    },
    {
      threshold: props.intersectionThreshold,
      rootMargin: "0px",
    },
  );

  observer.observe(lottieContainer.value);
};

onMounted(() => {
  createAnimation();
  setupIntersectionObserver();
});

onUnmounted(() => {
  if (animationInstance) {
    animationInstance.destroy();
  }
  if (observer) {
    observer.disconnect();
  }
});

watch(
  () => props.animationData,
  (newData) => {
    if (animationInstance) {
      animationInstance.destroy();
    }
    if (observer) {
      observer.disconnect();
    }

    if (lottieContainer.value && newData) {
      createAnimation();
      setupIntersectionObserver();
    }
  },
);

// Watch for changes in scrollTrigger prop
watch(
  () => props.scrollTrigger,
  () => {
    if (observer) {
      observer.disconnect();
    }
    setupIntersectionObserver();
  },
);
</script>
